import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getAllNotifications } from "api-client";

const PAGE_SIZE = 20;

// Prefix shared by the head and list query keys below, for bulk invalidation.
export function getNotificationsQueryKey() {
  return ["notifications"] as const;
}

function getNotificationsHeadQueryKey() {
  return [...getNotificationsQueryKey(), "head"] as const;
}

function getNotificationsListQueryKey() {
  return [...getNotificationsQueryKey(), "list"] as const;
}

export function useNotificationsQuery() {
  const headQuery = useQuery({
    queryKey: getNotificationsHeadQueryKey(),
    queryFn: async () => {
      const response = await getAllNotifications({
        query: { limit: PAGE_SIZE },
      });
      return response.data.data;
    },
  });

  const listQuery = useInfiniteQuery({
    queryKey: getNotificationsListQueryKey(),
    queryFn: async (context) => {
      const response = await getAllNotifications({
        query: { cursor: context.pageParam, limit: PAGE_SIZE },
      });
      return response.data.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor;
    },
    select: (data) => {
      return data.pages.flatMap((page) => {
        return page.notifications;
      });
    },
  });

  const headNotifications = headQuery.data?.notifications ?? [];
  const listNotifications = listQuery.data ?? [];

  // Dedupe by id: head and list page 1 can overlap and drift out of sync.
  const seenIds = new Set<string>();
  const notifications = [...headNotifications, ...listNotifications].filter(
    (notification) => {
      if (seenIds.has(notification.id)) {
        return false;
      }

      seenIds.add(notification.id);
      return true;
    },
  );

  return {
    notifications: notifications,
    unreadCount: headQuery.data?.unreadCount ?? 0,
    hasNextPage: listQuery.hasNextPage,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    fetchNextPage: listQuery.fetchNextPage,
  };
}
